import Box from '@mui/material/Box';
import { PropsWithChildren, Suspense, useMemo } from 'react';
import {
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  createMemoryRouter,
} from 'react-router-dom';
import {
  useAppSelector,
  useCheckColorPreference,
  useCheckDeviceSize,
} from './app/hooks';
import { selectLeoCloudUnderMaintenance } from '@features/view/viewSlice';
import AppLayout from './components/Layout/AppLayout';
import AuthLayout from './components/Layout/AuthLayout';
import PrivateRoute from './components/ReactRouter/PrivateRoute';
import Loading from './components/Utility/Loading';
import RouterErrorBoundary from './components/Utility/RouterErrorBoundary';
import {
  selectIsAuthenticated,
  selectIsGlobalAdmin,
} from './features/auth/authSlice';
import useCheckDomainAndRedirect from './hooks/appHooks/useCheckDomainAndRedirect';
import DoesNotExist from './pages/404/DoesNotExist';
import ForgotPassword from './pages/Auth/ForgotPassword/ForgotPassword';
import ForgotPasswordReset from './pages/Auth/ForgotPassword/ForgotPasswordReset';
import Login from './pages/Auth/Login/Login';
import TwoFactorCode from './pages/Auth/Login/TwoFactorCode';
import ResetPassword from './pages/Auth/ResetPassword/ResetPassword';
import ResetUsername from './pages/Auth/ResetUsername/ResetUsername';
import Help from './pages/Help/Help';
import SitePageWrapper from './pages/Site/SitePage/SitePageWrapper';
import lazyWithRetry from './utils/lazyWithRetry';
import useMaintenance from '@hooks/appHooks/useMaintenance';
import MaintenancePage from '@components/Maintenance/MaintenancePage';
import useAppInitRequests from '@hooks/appHooks/useAppInitRequests';
import { isTestServer } from '@constants';

const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/Dashboard'));

const AdminPortalWrapper = lazyWithRetry(
  () => import('./pages/AdminPortal/AdminPortalWrapper'),
);

const UserAccountWrapper = lazyWithRetry(
  () => import('./pages/UserAccount/UserAccountWrapper'),
);

const AllSiteAlarmsWrapper = lazyWithRetry(
  () => import('./pages/AllSiteAlarms/AllSiteAlarmsWrapper'),
);

const PerformanceReportsWrapper = lazyWithRetry(
  () => import('./pages/PerformanceReports/PerformanceReportsWrapper'),
);

const AlarmActivityWrapper = lazyWithRetry(
  () => import('./pages/AlarmActivity/AlarmActivityWrapper'),
);

const OfflineSitesWrapper = lazyWithRetry(
  () => import('./pages/OfflineSites/OfflineSitesWrapper'),
);

const SiteListWrapper = lazyWithRetry(
  () => import('./pages/SiteList/SiteListWrapper'),
);

const ControllersViewWrapper = lazyWithRetry(
  () => import('./pages/Site/Controllers/ControllersViewWrapper'),
);

const ControllerBackupsPageWrapper = lazyWithRetry(
  () => import('./pages/Site/ControllerBackups/ControllerBackupsPageWrapper'),
);

const ApplicationsGraphWrapper = lazyWithRetry(
  () =>
    import('./pages/Site/Graphs/ApplicationGraphs/ApplicationsGraphWrapper'),
);

const SiteAnalysisPageWrapper = lazyWithRetry(
  () => import('./pages/Site/SiteAnalysis/SiteAnalysisPageWrapper'),
);

const SiteAlarmsWrapper = lazyWithRetry(
  () => import('./pages/Site/SiteAlarms/SiteAlarmsWrapper'),
);

const SiteConfigPageWrapper = lazyWithRetry(
  () => import('./pages/Site/SiteSettings/SiteSettingsPageWrapper'),
);

const FloorplansWrapper = lazyWithRetry(
  () => import('./pages/Site/Floorplans/FloorplansWrapper'),
);

const MapsWrapper = lazyWithRetry(
  () => import('./pages/Site/Maps/MapsWrapper'),
);

const RefrigerantSystemsWrapper = lazyWithRetry(
  () => import('./pages/Site/RefrigerantSystems/RefrigerantSystemsWrapper'),
);

const RemoteAccessWrapper = lazyWithRetry(
  () => import('./pages/Site/RemoteAccess/RemoteAccessWrapper'),
);

const FullScreenRemoteAccessWrapper = lazyWithRetry(
  () => import('./pages/Site/RemoteAccess/FullScreenRemoteAccessWrapper'),
);

const CustomGraphsWrapper = lazyWithRetry(
  () => import('./pages/Site/Graphs/CustomGraphs/CustomGraphsWrapper'),
);

const LeoAlertLogsWrapper = lazyWithRetry(
  () => import('./pages/LeoAlertRecords/LeoAlertRecordsWrapper'),
);

const SuspenseWithFallback = ({ children }: PropsWithChildren) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

function AppRouter() {
  const isGlobalAdmin = useAppSelector(selectIsGlobalAdmin);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLeoCloudUnderMaintenance = useAppSelector(
    selectLeoCloudUnderMaintenance,
  );

  useCheckDomainAndRedirect();
  useCheckColorPreference();
  useCheckDeviceSize();
  useMaintenance();

  const { isLoading } = useAppInitRequests();

  const routes = useMemo<RouteObject[]>(() => {
    return [
      {
        element: <AuthLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          { path: 'login', Component: Login },
          { path: 'twoFactorAuthCode', Component: TwoFactorCode },
          { path: 'two-factor-auth', Component: TwoFactorCode },
          { path: 'resetPassword', Component: ResetPassword },
          { path: 'resetUsername', Component: ResetUsername },
          { path: 'forgotPassword', Component: ForgotPassword },
          {
            path: 'forgot-my-password-reset',
            Component: ForgotPasswordReset,
          },
        ],
      },
      {
        path: '/',
        Component: () => (
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            authenticationPath="/login"
            outlet={<AppLayout />}
          />
        ),
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            index: true,
            element: (
              <SuspenseWithFallback>
                <Dashboard />
              </SuspenseWithFallback>
            ),
          },
          /* PATHS FOR SITES */
          {
            path: 'site/:siteId/:siteNameAndLocation',
            Component: SitePageWrapper,
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/settings/:tab?',
            element: (
              <SuspenseWithFallback>
                <SiteConfigPageWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/alarms/:tab?/:duration?/:durationAmount?',
            element: (
              <SuspenseWithFallback>
                <SiteAlarmsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/analysis/:tab?',
            element: (
              <SuspenseWithFallback>
                <SiteAnalysisPageWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/terminalMode/:device?',
            element: (
              <SuspenseWithFallback>
                <RemoteAccessWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/terminalModeFullScreen/:device?',
            element: (
              <SuspenseWithFallback>
                <FullScreenRemoteAccessWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'leo/:siteId/:leoName/terminalMode/:device?',
            element: (
              <SuspenseWithFallback>
                <RemoteAccessWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'leo/:siteId/:leoName/terminalModeFullScreen/:device?',
            element: (
              <SuspenseWithFallback>
                <FullScreenRemoteAccessWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/floorplans',
            element: (
              <SuspenseWithFallback>
                <FloorplansWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/maps/:tab?',
            element: (
              <SuspenseWithFallback>
                <MapsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/assetTracking/:tab?',
            element: (
              <SuspenseWithFallback>
                <RefrigerantSystemsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/customGraph/:startDate?/:endDate?',
            element: (
              <SuspenseWithFallback>
                <CustomGraphsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/controllerBackups/:tab',
            element: (
              <SuspenseWithFallback>
                <ControllerBackupsPageWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/controller/:controllerName',
            element: (
              <SuspenseWithFallback>
                <ControllersViewWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/controller/:controllerName/graph/:applicationName/:startDate?/:endDate?',
            element: (
              <SuspenseWithFallback>
                <ApplicationsGraphWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'site/:siteId/:siteNameAndLocation/leoAlertRecords/:tab?/:startDate?/:endDate?',
            element: (
              <SuspenseWithFallback>
                <LeoAlertLogsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'leoAlertRecords/:tab?/:startDate?/:endDate?',
            element: (
              <SuspenseWithFallback>
                <LeoAlertLogsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'allSitesAlarms/:tab?/:duration?/:durationAmount?',
            element: (
              <SuspenseWithFallback>
                <AllSiteAlarmsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'alarmActivity',
            element: (
              <SuspenseWithFallback>
                <AlarmActivityWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'offlineSites/:tab?',
            element: (
              <SuspenseWithFallback>
                <OfflineSitesWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'siteList',
            element: (
              <SuspenseWithFallback>
                <SiteListWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'adminPortal/:tab?',
            element: (
              <SuspenseWithFallback>
                <AdminPortalWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            // Legacy Route (use to be called 'Analytics')
            path: 'analytics/:date?/:report?',
            element: (
              <SuspenseWithFallback>
                <PerformanceReportsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'performanceReports/:date?/:report?',
            element: (
              <SuspenseWithFallback>
                <PerformanceReportsWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: 'help', // Not currently used
            Component: Help,
          },
          {
            path: 'userAccount/:tab?',
            element: (
              <SuspenseWithFallback>
                <UserAccountWrapper />
              </SuspenseWithFallback>
            ),
          },
          {
            path: '*',
            Component: DoesNotExist,
          },
        ],
      },
    ];
  }, [isAuthenticated]);

  const router = useMemo(() => {
    if (import.meta.env.VITEST_POOL_ID !== undefined) {
      return createMemoryRouter(routes);
    }
    return createBrowserRouter(routes);
  }, [routes]);

  if (isLeoCloudUnderMaintenance && !isGlobalAdmin && !isTestServer) {
    return <MaintenancePage />;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
        }}
        data-testid="permissionsLoading"
      >
        <Loading />
      </Box>
    );
  }

  return <RouterProvider router={router} fallbackElement={<Loading />} />;
}

export default AppRouter;
